import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  TextField,
  MenuItem,
} from '@mui/material';
import type {
  CustomerVendorSubscription,
  SubscriptionSeatChange,
  CustomerIndexItemDto,
} from '@techgitter/contracts';
import { getVendorSubscriptions } from '../services/vendorSubscriptionService';
import { fetchSeatChanges } from '../services/seatLedgerApi';
import { useSeatHistory } from '../hooks/useSeatHistory';
import { useCurrentUser } from '../contexts/CurrentUserContext';
import { CustomerLookupControl } from '../components/tickets/Customer/CustomerLookupControl';
import { CARD_SX } from '../styles/designSystem';
import {
  DataTable,
  DataTableCard,
  DataTableHeadCell,
  DataTableRow,
  DataTableCell,
  DataTableEmpty,
} from '../components/table';

type TabKey = 'history' | 'changes';

const fmtDate = (iso?: string | null): string =>
  iso ? new Date(iso).toLocaleDateString() : '—';

const subLabel = (s: CustomerVendorSubscription): string => {
  const product = s.vendorProductName ?? s.vendorProductId;
  const seats = `${s.seats} seat${s.seats === 1 ? '' : 's'}`;
  return s.subscriptionRef ? `${product} · ${seats} · ${s.subscriptionRef}` : `${product} · ${seats}`;
};

/**
 * Seat Ledger — admin point-in-time seat report (Phase 1). Find a customer via
 * the type-ahead, pick one of their subscriptions, and see its committed /
 * provisioned / obligation / billed series plus the raw change log. Reads the
 * same commitment the reconciliation controls use.
 */
export const SeatLedgerPage: React.FC = () => {
  const currentUser = useCurrentUser();
  const isAdmin = !!currentUser?.hasAnyRole('Admin');

  const [customer, setCustomer] = useState<CustomerIndexItemDto | null>(null);
  const [customerSubs, setCustomerSubs] = useState<CustomerVendorSubscription[]>([]);
  const [subsLoading, setSubsLoading] = useState(false);
  const [subsError, setSubsError] = useState<string | null>(null);
  const [subscriptionId, setSubscriptionId] = useState<string>('');
  const [tab, setTab] = useState<TabKey>('history');

  const { points, isLoading: historyLoading, error: historyError } = useSeatHistory(
    subscriptionId || null,
  );

  const [changes, setChanges] = useState<SubscriptionSeatChange[]>([]);
  const [changesLoading, setChangesLoading] = useState(false);
  const [changesError, setChangesError] = useState<string | null>(null);

  // When a customer is picked, load all of their subscriptions.
  useEffect(() => {
    if (!customer) {
      setCustomerSubs([]);
      setSubscriptionId('');
      return;
    }
    const controller = new AbortController();
    setSubsLoading(true);
    setSubsError(null);
    setSubscriptionId('');
    getVendorSubscriptions({ customerId: customer.dataverseCustomerId }, controller.signal)
      .then((items) => {
        if (controller.signal.aborted) return;
        setCustomerSubs(items);
        // Auto-select when the customer has a single subscription.
        if (items.length === 1) setSubscriptionId(items[0].id);
      })
      .catch((err) => {
        if (!controller.signal.aborted) {
          setSubsError(err instanceof Error ? err.message : String(err));
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setSubsLoading(false);
      });
    return () => controller.abort();
  }, [customer]);

  // Change log for the selected subscription.
  useEffect(() => {
    if (!subscriptionId) {
      setChanges([]);
      return;
    }
    const controller = new AbortController();
    setChangesLoading(true);
    setChangesError(null);
    fetchSeatChanges({ subscriptionId }, controller.signal)
      .then((items) => {
        if (!controller.signal.aborted) setChanges(items);
      })
      .catch((err) => {
        if (!controller.signal.aborted) {
          setChangesError(err instanceof Error ? err.message : String(err));
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setChangesLoading(false);
      });
    return () => controller.abort();
  }, [subscriptionId]);

  if (!isAdmin) {
    return (
      <Alert severity="warning" sx={{ m: 3 }}>
        The seat ledger is available to administrators only.
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={[CARD_SX, { p: 2, mb: 2 }]}>
        <Box sx={{ maxWidth: 480 }}>
          <CustomerLookupControl value={customer} onChange={setCustomer} />
        </Box>

        {customer && (
          <>
            {subsError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {subsError}
              </Alert>
            )}
            {subsLoading ? (
              <CircularProgress size={24} sx={{ mt: 2 }} />
            ) : customerSubs.length === 0 ? (
              <Alert severity="info" sx={{ mt: 2 }}>
                {customer.displayName} has no vendor subscriptions.
              </Alert>
            ) : (
              <TextField
                select
                size="small"
                label="Subscription"
                value={subscriptionId}
                onChange={(e) => setSubscriptionId(e.target.value)}
                sx={{ minWidth: 380, mt: 2 }}
              >
                <MenuItem value="">
                  <em>Select a subscription…</em>
                </MenuItem>
                {customerSubs.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {subLabel(s)}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </>
        )}
      </Box>

      <Paper variant="outlined" sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={(_, v: TabKey) => setTab(v)}>
          <Tab value="history" label="Seat History" />
          <Tab value="changes" label="Change Log" />
        </Tabs>
      </Paper>

      {!subscriptionId ? (
        <Alert severity="info">
          {customer
            ? 'Choose a subscription to view its seat history and change log.'
            : 'Find a customer to begin.'}
        </Alert>
      ) : tab === 'history' ? (
        historyLoading ? (
          <CircularProgress />
        ) : (
          <>
            {historyError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {historyError}
              </Alert>
            )}
            <DataTableCard>
              <Box sx={{ overflowX: 'auto' }}>
                <DataTable>
                  <thead>
                    <tr>
                      <DataTableHeadCell>Date</DataTableHeadCell>
                      <DataTableHeadCell align="right">Committed</DataTableHeadCell>
                      <DataTableHeadCell align="right">Provisioned</DataTableHeadCell>
                      <DataTableHeadCell align="right">Obligation</DataTableHeadCell>
                      <DataTableHeadCell align="right">Billed</DataTableHeadCell>
                    </tr>
                  </thead>
                  <tbody>
                    {points.length === 0 ? (
                      <DataTableEmpty colSpan={5}>
                        No seat history for this subscription yet.
                      </DataTableEmpty>
                    ) : (
                      points.map((p, i) => (
                        <DataTableRow key={`${p.asOf}-${i}`}>
                          <DataTableCell>{fmtDate(p.asOf)}</DataTableCell>
                          <DataTableCell align="right">{p.committedSeats}</DataTableCell>
                          <DataTableCell align="right">{p.provisionedSeats}</DataTableCell>
                          <DataTableCell align="right" bold>
                            {p.obligationSeats}
                          </DataTableCell>
                          <DataTableCell align="right" muted>
                            {p.billedSeats ?? '—'}
                          </DataTableCell>
                        </DataTableRow>
                      ))
                    )}
                  </tbody>
                </DataTable>
              </Box>
            </DataTableCard>
          </>
        )
      ) : changesLoading ? (
        <CircularProgress />
      ) : (
        <>
          {changesError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {changesError}
            </Alert>
          )}
          <DataTableCard>
            <Box sx={{ overflowX: 'auto' }}>
              <DataTable>
                <thead>
                  <tr>
                    <DataTableHeadCell>Effective</DataTableHeadCell>
                    <DataTableHeadCell align="right">Δ</DataTableHeadCell>
                    <DataTableHeadCell>Reason</DataTableHeadCell>
                    <DataTableHeadCell>Source</DataTableHeadCell>
                    <DataTableHeadCell>Ticket</DataTableHeadCell>
                    <DataTableHeadCell>By</DataTableHeadCell>
                  </tr>
                </thead>
                <tbody>
                  {changes.length === 0 ? (
                    <DataTableEmpty colSpan={6}>
                      No seat changes recorded.
                    </DataTableEmpty>
                  ) : (
                    changes.map((c) => (
                      <DataTableRow key={c.id}>
                        <DataTableCell>{fmtDate(c.effectiveDate)}</DataTableCell>
                        <DataTableCell align="right" bold>
                          {c.delta > 0 ? `+${c.delta}` : c.delta}
                        </DataTableCell>
                        <DataTableCell>{c.reason}</DataTableCell>
                        <DataTableCell muted>{c.source}</DataTableCell>
                        <DataTableCell muted>{c.sourceTicketRef ?? '—'}</DataTableCell>
                        <DataTableCell muted>{c.requestedBy ?? '—'}</DataTableCell>
                      </DataTableRow>
                    ))
                  )}
                </tbody>
              </DataTable>
            </Box>
          </DataTableCard>
        </>
      )}
    </Box>
  );
};

export default SeatLedgerPage;
